import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Column, Row, Text, Icon } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { useAppDispatch } from "src/store/store";
import { hydrateFromDraft } from "src/store/workflowSlice";
import {
    resetInspectionCaptureState,
    setScannedBarcode,
} from "src/store/inspectionCaptureSlice";
import { ResumePosition } from "../utils/resumePosition";
import ThemedButton from "./ThemedButton";
import "./ResumeConfirmationPage.scss";


export interface ResumeConfirmationState {
    inspectionId: string;
    resumePosition: ResumePosition;
}

/** Zero-pad a container count to 2 digits (e.g. 3 -> "03"). */
const padCount = (count: number | undefined): string =>
    String(count ?? 0).padStart(2, "0");


const ResumeConfirmationPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();

    const state = location.state as ResumeConfirmationState | null;

    // Defensive: if this route is hit without the expected location state
    // (e.g. a direct deep link or refresh), there is nothing to confirm.
    if (!state || !state.resumePosition || !state.inspectionId) {
        return (
            <Column
                height="100vh"
                alignmentVertical="center"
                alignmentHorizontal="center"
            >
                <Text type="h200">Unable to resume inspection.</Text>
            </Column>
        );
    }

    const { inspectionId, resumePosition } = state;

    const handleBack = () => {
        navigate(-1);
    };

    const handleResume = () => {
        // Same dispatch sequence as the former ResumeDraftPage non-complete
        // branch. Ordering matters — `resetInspectionCaptureState()` clears
        // `scannedBarcode`, so it MUST run before `setScannedBarcode(...)`.
        dispatch(resetInspectionCaptureState());
        if (resumePosition.scannedBarcode) {
            dispatch(setScannedBarcode(resumePosition.scannedBarcode));
        }
        dispatch(
            hydrateFromDraft({
                inspectionId,
                categoryIndex: resumePosition.categoryIndex,
                stepIndex: resumePosition.stepIndex,
                iterateStepIndex: resumePosition.iterateStepIndex,
                completedContainerCount: resumePosition.completedContainerCount,
            })
        );
        navigate("/configurationDrivenInspections/execute");
    };

    const completedCount = padCount(resumePosition.completedContainerCount);
    // In-progress container (Rule 2) when a barcode is being resumed; otherwise
    // between-containers (Rule 3).
    const isInProgress = resumePosition.scannedBarcode != null;
    // Abandoned before the first container captured anything: no completed
    // containers and no in-progress barcode. Show a plain "start over" message
    // instead of a "00 containers" / empty last-container line.
    const hasNoProgress = !resumePosition.completedContainerCount && resumePosition.scannedBarcode == null;

    return (
        <div className="resume-confirmation-page pwa-scrollable">
            <Column spacingInset="500" spacing="500">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="resume-confirmation-back-button"
                        onClick={handleBack}
                        role="button"
                    >
                        <Icon tokens={arrowLeftTokens} color="success" />
                    </div>
                    <Text type="b300" color="success">
                        Resume Inspection
                    </Text>
                </Row>

                <Column spacing="400">
                    {hasNoProgress ? (
                        <Text type="b400" color="primary">
                            This inspection hasn't captured any containers yet.
                            Resume to begin with the first container.
                        </Text>
                    ) : (
                        <>
                            <Text type="b400" color="primary">
                                You have completed inspection of{" "}
                                <strong>{completedCount} containers.</strong>
                            </Text>

                            {isInProgress ? (
                                <Text type="b400" color="primary">
                                    Resume capturing container{" "}
                                    <strong>{resumePosition.scannedBarcode}.</strong>
                                </Text>
                            ) : (
                                <Text type="b400" color="primary">
                                    Last completely inspected container{" "}
                                    <strong>{resumePosition.lastCompletedBarcode}.</strong> Please
                                    resume inspection from next container.
                                </Text>
                            )}
                        </>
                    )}
                </Column>
            </Column>

            {/* Spacer so content is not hidden behind the fixed footer. */}
            <Row minHeight="80px" />

            <Row
                className="resume-confirmation-page__footer"
                spacingInset="400"
                width="100%"
                widths="fill"
            >
                <ThemedButton onClick={handleResume}>
                    RESUME INSPECTION
                </ThemedButton>
            </Row>
        </div>
    );
};

export default ResumeConfirmationPage;
